import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNativeLayout } from '../../hooks/useNativeLayout';
import { workspaceRegions, type LayoutRect, type LayoutSnapshotV1 } from '../../utils/nativeLayout';
import { acquireBodyScrollLock } from '../../utils/bodyScrollLock';
import './adaptive-workspace.css';

/** One document and stable panel nodes, even when a reserved region changes axis. */
export function AdaptiveWorkspace({ primary, secondary, primaryLabel, secondaryLabel, className = '' }: {
  primary: React.ReactNode; secondary: React.ReactNode; primaryLabel: string; secondaryLabel: string; className?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const layout = useNativeLayout();
  const [frame, setFrame] = useState<LayoutRect>({ x: 0, y: 0, width: window.innerWidth, height: window.innerHeight });
  const [browserLayout, setBrowserLayout] = useState<LayoutSnapshotV1 | null>(null);
  useLayoutEffect(() => {
    const update = () => {
      if (!root.current) return;
      const rect = root.current.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const next = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
      setFrame(previous => Object.keys(next).every(k => previous[k as keyof LayoutRect] === next[k as keyof LayoutRect]) ? previous : next);
      const css = getComputedStyle(root.current);
      const viewport = window.visualViewport;
      const fallback: LayoutSnapshotV1 = { version: 1, sceneId: 'browser', revision: 0,
        bounds: { width: window.innerWidth, height: window.innerHeight },
        safeInsets: { top: Math.max(parseFloat(css.paddingTop) || 0, viewport?.offsetTop ?? 0),
          left: Math.max(parseFloat(css.paddingLeft) || 0, viewport?.offsetLeft ?? 0),
          bottom: Math.max(parseFloat(css.paddingBottom) || 0, viewport ? window.innerHeight - viewport.height - viewport.offsetTop : 0),
          right: Math.max(parseFloat(css.paddingRight) || 0, viewport ? window.innerWidth - viewport.width - viewport.offsetLeft : 0) },
        reservedRegions: [], verticalBarEdge: 'none', keyboardOcclusions: [], keyboardInset: 0, fontScale: 1,
        capabilities: { reservedRegions: false, cameraDirection: false } };
      setBrowserLayout(previous => JSON.stringify(previous) === JSON.stringify(fallback) ? previous : fallback);
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (root.current) observer?.observe(root.current);
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('scroll', update, true);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); window.visualViewport?.removeEventListener('resize', update); window.visualViewport?.removeEventListener('scroll', update); window.removeEventListener('scroll', update, true); };
  }, []);
  const regions = workspaceRegions(frame, layout ?? browserLayout);
  useLayoutEffect(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.current?.contains(active)) active.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [layout?.revision]);
  const style = (r: LayoutRect): React.CSSProperties => ({ left: r.x, top: r.y, width: r.width, height: r.height });
  const single = regions.mode === 'single';
  return <div ref={root} className={`adaptive-workspace ${className}`} data-arrangement={regions.mode}
    style={{ '--bilt-workspace-font-scale': Math.max(1, layout?.fontScale ?? 1) } as React.CSSProperties}>
    <div className="adaptive-workspace__canvas" style={single ? style(regions.primary) : undefined}>
      <section className="adaptive-workspace__primary" aria-label={primaryLabel} style={{ ...(single ? {} : style(regions.primary)), '--bilt-panel-height': `${regions.primary.height}px` } as React.CSSProperties}>{primary}</section>
      <section className="adaptive-workspace__secondary" aria-label={secondaryLabel} style={single ? undefined : style(regions.secondary!)}>{secondary}</section>
    </div>
  </div>;
}

/** Native HTML modality keeps underlying collection state mounted and inaccessible. */
export function AdaptiveWorkspaceDialog({ title, primary, secondary, onClose, className = '' }: {
  title: string; primary: React.ReactNode; secondary: React.ReactNode; onClose: () => void; className?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const element = dialog.current;
    const release = acquireBodyScrollLock();
    if (element?.showModal) element.showModal();
    else element?.setAttribute('open', '');
    heading.current?.focus({ preventScroll: true });
    return () => { element?.close?.(); release(); opener?.focus({ preventScroll: true }); };
  }, []);
  const contents = <dialog ref={dialog} className={`adaptive-dialog ${className}`} aria-label={title}
    onCancel={event => { event.preventDefault(); closeRef.current(); }}>
    <AdaptiveWorkspace primaryLabel={title} secondaryLabel={`${title} controls`}
      primary={<><h2 ref={heading} tabIndex={-1} className="adaptive-workspace__title">{title}</h2>{primary}</>}
      secondary={<><div className="adaptive-workspace__actions"><button type="button" onClick={onClose}>Close {title}</button></div>{secondary}</>} />
  </dialog>;
  return createPortal(contents, document.body);
}
